import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Bar
} from 'recharts';
import './CandleStick.css';

/** Format to YYYY-MM-DD (safe if already ISO). */
const formatISODate = (dateStr) => {
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return dateStr.slice(0, 10);
  }
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const CandleStick = ({ chartData, stockInfo, selectedPeriod }) => {
  if (!chartData || !chartData.length) return <p className="no-results">No data to display.</p>;

  // Find min and max values for domain calculation
  const highestPrice = Math.max(...chartData.map(item => item.high));
  const lowestPrice = Math.min(...chartData.map(item => item.low));
  const pricePadding = (highestPrice - lowestPrice) * 0.1; // 10% padding

  // Calculate custom ticks that increment by 5
  const minTick = Math.floor((lowestPrice - pricePadding) / 5) * 5;
  const maxTick = Math.ceil((highestPrice + pricePadding) / 5) * 5;
  const customTicks = [];
  for (let i = minTick; i <= maxTick; i += 5) {
    customTicks.push(i);
  }

  // Month vs Year x-axis logic (parity with VWAP/Volume/Change%)
  const showYears = ['3Y', '5Y', 'MAX'].includes(selectedPeriod);
  const xTicks = showYears
    ? Array.from(new Set(chartData.map(d => new Date(d.date).getFullYear())))
        .map(year => chartData.find(d => new Date(d.date).getFullYear() === year)?.date)
        .filter(Boolean)
    : undefined;

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="candlestick-tooltip">
          <p className="date"><b>{formatISODate(data.date)}</b></p>
          <p className="open">Open: ${data.open.toFixed(2)}</p>
          <p className="close">Close: ${data.close.toFixed(2)}</p>
          <p className="high">High: ${data.high.toFixed(2)}</p>
          <p className="low">Low: ${data.low.toFixed(2)}</p>
          <p className="volume">Volume: {data.volume.toLocaleString()}</p>
        </div>
      );
    }
    return null;
  };

  // Prepare data with additional calculated properties
  const processedData = chartData.map(item => {
    const isUp = item.close >= item.open;
    return {
      ...item,
      // For correct rendering in the bar chart
      value: [item.low, item.high], // This creates a proper range for the bar
      isUp
    };
  });

  return (
    <div className="chart-content">
      <h3>Candlestick Chart - {stockInfo.name} ({stockInfo.ticker})</h3>

      <div className="candlestick-chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={processedData}
            margin={{ top: 20, right: 30, left: 50, bottom: 30 }}
          >
            <CartesianGrid strokeDasharray="3 3" />

            <XAxis
              dataKey="date"
              scale="band"
              padding={{ left: 10, right: 10 }}
              ticks={xTicks}
              tickFormatter={(value) => {
                const d = new Date(value);
                return showYears
                  ? d.getFullYear().toString()
                  : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              }}
              tick={{ fontSize: 12 }}
            />

            <YAxis
              domain={[lowestPrice - pricePadding, highestPrice + pricePadding]}
              tickFormatter={(value) => `$${value.toFixed(0)}`}
              ticks={customTicks}
              tick={{ fontSize: 12 }}
            />

            <Tooltip content={<CustomTooltip />} />

            <Bar
              dataKey="value"
              name=""
              isAnimationActive={false}
              legendType="none"
              shape={(props) => {
                if (!props || !props.payload) return null;

                const { x, y, width, height, payload } = props;
                const { isUp, open, close, high, low } = payload;
                const color = isUp ? '#22c55e' : '#ef4444';

                // Chart is inverted (y=0 at top), so we need to calculate positions
                // Get the Y coordinate scale
                const yScale = height / (high - low);

                // Calculate all Y positions relative to the bar's position
                const xCenter = x + width / 2;
                const yHigh = y;
                const yLow = y + height;
                const yOpen = y + (high - open) * yScale;
                const yClose = y + (high - close) * yScale;

                // Calculate body coordinates
                const bodyTop = Math.min(yOpen, yClose);
                const bodyBottom = Math.max(yOpen, yClose);
                const bodyHeight = Math.max(1, bodyBottom - bodyTop); // At least 1px tall
                const bodyWidth = width * 0.6; // 60% of available width

                return (
                  <g>
                    {/* High to Low vertical line (the wick) */}
                    <line
                      x1={xCenter}
                      y1={yHigh}
                      y2={yLow}
                      x2={xCenter}
                      stroke={color}
                      strokeWidth={1}
                      className={isUp ? 'bullish wick' : 'bearish wick'}
                    />

                    {/* Open-Close rectangle (the body) */}
                    <rect
                      x={xCenter - bodyWidth / 2}
                      y={bodyTop}
                      width={bodyWidth}
                      height={bodyHeight}
                      fill={color}
                      className={isUp ? 'bullish body' : 'bearish body'}
                    />
                  </g>
                );
              }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CandleStick;
